import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { ChevronLeft, Lightbulb, Loader2, RotateCcw, Target, Pencil } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

import { api } from "../lib/api";
import useRecorder from "../hooks/useRecorder";
import RecordButton from "../components/RecordButton";
import Waveform from "../components/Waveform";
import GoalDialog from "../components/GoalDialog";
import { Button } from "../components/ui/button";
import { trackEvent } from "../lib/track";

const fmt = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

export default function Practice() {
    const { promptId } = useParams();
    const [search] = useSearchParams();
    const navigate = useNavigate();

    const [prompt, setPrompt] = useState(null);
    const [goal, setGoal] = useState(null);
    const [goalOpen, setGoalOpen] = useState(false);
    const [phase, setPhase] = useState("idle");
    const [hasRecorded, setHasRecorded] = useState(false);

    const recorder = useRecorder();

    useEffect(() => {
        if (promptId === "custom" || !promptId) {
            setPrompt({
                id: "custom",
                category: "Custom",
                title: search.get("title") || "Custom prompt",
                text: search.get("text") || "Speak on the topic you've chosen.",
                target_seconds: 90,
                tips: ["Open with a clear statement", "Land one specific story", "End with a memorable line"],
            });
            return;
        }
        api.get(`/prompts/${promptId}`)
            .then(({ data }) => setPrompt(data))
            .catch(() => {
                toast.error("Prompt not found");
                navigate("/home", { replace: true });
            });
    }, [promptId, search, navigate]);

    useEffect(() => {
        if (!prompt) return;
        api.get(`/goals/${prompt.id}`)
            .then(({ data }) => setGoal(data))
            .catch(() => {});
    }, [prompt]);

    const targetSec = goal?.target_duration_sec || prompt?.target_seconds || 90;
    const overTime = recorder.seconds > targetSec + 30;

    const start = async () => {
        try {
            await recorder.start();
            setPhase("recording");
            trackEvent("session_started", { prompt_id: prompt?.id });
        } catch {
            toast.error("Couldn't access your microphone. Check permissions and try again.");
        }
    };

    const stopAndSubmit = async () => {
        const result = await recorder.stop();
        if (!result || !result.blob) return;
        if (result.durationSec < 2) {
            toast.error("Recording too short — try again.");
            setPhase("idle");
            return;
        }
        setHasRecorded(true);
        setPhase("uploading");

        const fd = new FormData();
        const ext = result.mimeType?.includes("mp4") ? "mp4" : "webm";
        fd.append("audio", result.blob, `answer.${ext}`);
        fd.append("prompt_id", prompt.id);
        fd.append("duration_sec", String(result.durationSec));
        if (prompt.id === "custom") {
            fd.append("custom_prompt_text", prompt.text || "");
            fd.append("custom_prompt_title", prompt.title || "Custom prompt");
        }
        try {
            setPhase("analyzing");
            const { data } = await api.post("/sessions/practice", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 120000,
            });
            trackEvent("session_completed", {
                prompt_id: prompt.id,
                duration_sec: result.durationSec,
                score: data?.analysis?.overall_score,
            });
            navigate(`/results/${data.id}`, { replace: true });
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || "Could not analyze your answer.";
            trackEvent("session_failed", { prompt_id: prompt.id, reason: msg?.slice(0, 120) });
            toast.error(msg);
            setPhase("idle");
            setHasRecorded(false);
        }
    };

    const phaseLabel = useMemo(() => {
        if (phase === "uploading") return "Uploading…";
        if (phase === "analyzing") return "Coaching in progress…";
        if (phase === "recording") return "Listening";
        return "Ready when you are";
    }, [phase]);

    if (!prompt) {
        return <div className="min-h-[60vh] flex items-center justify-center text-stone-500 italic font-serif text-2xl">Loading…</div>;
    }

    const isBusy = phase === "uploading" || phase === "analyzing";

    return (
        <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-6" data-testid="practice-page">
            <Link to="/home" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-terracotta transition-colors" data-testid="practice-back">
                <ChevronLeft size={16} strokeWidth={1.5} /> Back
            </Link>

            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="mt-4"
            >
                <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-stone-400">{prompt.category}</div>
                <h1 className="font-serif text-3xl sm:text-5xl tracking-tight font-light mt-2 leading-[1.1]">
                    {prompt.title}
                </h1>
                <p className="text-base sm:text-lg text-stone-700 dark:text-stone-300 mt-4 leading-relaxed font-serif italic" data-testid="practice-prompt-text">
                    “{prompt.text}”
                </p>
            </motion.div>

            {/* Goals card */}
            {goal && (
                <div
                    className="mt-6 rounded-3xl border border-stone-200/80 dark:border-stone-800 bg-white/70 dark:bg-[#242322]/70 backdrop-blur-md p-5 sm:p-6"
                    data-testid="practice-goals-card"
                >
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-bold text-stone-400">
                            <Target size={12} strokeWidth={1.5} />
                            Your goals {goal.is_default && <span className="text-stone-300 normal-case tracking-normal text-[10px] italic">(defaults)</span>}
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setGoalOpen(true)}
                            className="rounded-full text-stone-500 hover:text-terracotta -mr-2 -my-1"
                            data-testid="goals-edit-btn"
                        >
                            <Pencil size={12} className="mr-1.5" /> Edit
                        </Button>
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-3 text-center">
                        <div>
                            <div className="font-serif text-2xl sm:text-3xl font-light" data-testid="goals-wpm">
                                {goal.target_wpm_min}–{goal.target_wpm_max}
                            </div>
                            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-400 mt-0.5">WPM</div>
                        </div>
                        <div>
                            <div className="font-serif text-2xl sm:text-3xl font-light" data-testid="goals-fillers">
                                ≤ {Number(goal.max_fillers_per_min)}
                            </div>
                            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-400 mt-0.5">Fillers/min</div>
                        </div>
                        <div>
                            <div className="font-serif text-2xl sm:text-3xl font-light" data-testid="goals-duration">
                                {fmt(goal.target_duration_sec)}
                            </div>
                            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-400 mt-0.5">Length</div>
                        </div>
                    </div>
                </div>
            )}

            {prompt.tips?.length > 0 && (
                <div className="mt-6 rounded-3xl border border-sage/20 bg-sage/5 dark:bg-sage/10 p-5">
                    <div className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-bold text-sage">
                        <Lightbulb size={12} strokeWidth={1.5} /> Three things to remember
                    </div>
                    <ul className="mt-3 space-y-1.5 text-sm text-stone-700 dark:text-stone-300">
                        {prompt.tips.map((t, i) => (
                            <li key={i} className="flex items-start gap-2">
                                <span className="text-sage mt-1.5">•</span>
                                <span>{t}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Recording stage */}
            <div className="mt-10 rounded-3xl border border-stone-200/80 dark:border-stone-800 bg-white/70 dark:bg-[#242322]/70 backdrop-blur-md p-8 text-center" data-testid="recorder-stage">
                <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-stone-400 mb-3" data-testid="recorder-phase">
                    {phaseLabel}
                </div>

                <div className="font-serif text-6xl sm:text-7xl font-light tracking-tight tabular-nums" data-testid="recorder-timer">
                    <span className={overTime ? "text-terracotta" : ""}>{fmt(recorder.seconds)}</span>
                    <span className="text-stone-300 dark:text-stone-700"> / {fmt(targetSec)}</span>
                </div>

                <div className="mt-6">
                    <Waveform levels={recorder.levels} active={phase === "recording"} />
                </div>

                <div className="mt-6 flex justify-center">
                    <AnimatePresence mode="wait">
                        {isBusy ? (
                            <motion.div
                                key="busy"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0 }}
                                className="flex flex-col items-center gap-3"
                                data-testid="recorder-busy"
                            >
                                <Loader2 size={36} className="text-terracotta animate-spin" strokeWidth={1.5} />
                                <p className="text-sm text-stone-500 italic font-serif text-base">
                                    {phase === "uploading" ? "Uploading your answer…" : "Whisper is transcribing. Your coach is reading carefully."}
                                </p>
                            </motion.div>
                        ) : (
                            <RecordButton
                                isRecording={phase === "recording"}
                                onToggle={() => (phase === "recording" ? stopAndSubmit() : start())}
                            />
                        )}
                    </AnimatePresence>
                </div>

                {phase === "recording" && (
                    <div className="mt-4">
                        <Button
                            variant="ghost"
                            onClick={() => {
                                recorder.cancel();
                                setPhase("idle");
                            }}
                            className="rounded-full text-stone-500 hover:text-stone-800"
                            data-testid="recorder-cancel"
                        >
                            <RotateCcw size={14} className="mr-1.5" /> Discard & restart
                        </Button>
                    </div>
                )}

                {phase === "idle" && !hasRecorded && (
                    <p className="mt-4 text-xs text-stone-400">
                        Tap the button, take a breath, and answer naturally. We'll handle the rest.
                    </p>
                )}
            </div>

            <GoalDialog
                open={goalOpen}
                onOpenChange={setGoalOpen}
                promptId={prompt.id}
                promptTitle={prompt.title}
                onSaved={(g) => setGoal(g)}
            />
        </div>
    );
}
